'use client';

import heroBackground from '@/public/service/heroBackground.png';
import heroBackgroundMobile from '@/public/service/heroBackgroundMobile.png';
import Image from 'next/image';
import { useRef } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton } from '@/sanity/lib/definitions';
import TrustChip from '@/components/global/TrustChip';
import { CTAButton } from '@/shadcn/ui/button';
import heroCard from '@/public/home/heroCard.png';

interface IHeroProps {
  title: PortableTextBlock[];
  buttonOne: GROQButton;
  buttonTwo: GROQButton;
}

export default function Hero({
  title,
  buttonOne,
  buttonTwo,
}: IHeroProps) {
  const isMobile = useIsMobile();

  return (
    <section className="w-full flex flex-col items-center lg:pt-[3rem]">
      <div className="relative flex flex-col items-center justify-end lg:justify-center w-full max-w-[96vw] lg:max-w-[230.01rem] min-h-[87.85738rem] lg:min-h-[91.49988rem] overflow-clip lg:px-[6rem] pb-[4.15rem] lg:py-[10rem]">
        <div className="container w-full relative z-10 flex flex-col gap-[3rem] lg:gap-[2.25rem] items-center justify-center px-[1rem] lg:px-0 lg:max-w-[70rem]">
          <motion.div
            className="flex flex-col gap-[2rem] lg:gap-[2.25rem] richtext richtext-center prose-headings:mb-0 prose-headings:leading-[110%] lg:prose-headings:leading-[120%] lg:prose-p:text-[2.25rem] lg:[&_br]:hidden"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: easeInOut, delay: 0.25 }}
            whileInView="visible"
            viewport={{ once: true }}
          >
            <PortableText value={title} components={richTextComponent} />
          </motion.div>

          <motion.div
            className="flex flex-col lg:flex-row gap-[1rem] lg:gap-[3rem] items-center justify-center lg:mt-[1.25rem] w-full"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, ease: easeInOut, delay: 0.5 }}
            whileInView="visible"
            viewport={{ once: true }}
          >
            <CTAButton data={buttonOne} className="w-full lg:w-auto" />
            <CTAButton data={buttonTwo} className="w-full lg:w-auto" />
          </motion.div>
        </div>

        {/* Background Start */}
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: '-175vw'	 }}
          transition={{ duration: 1.8, ease: [0.76, 0, 0.24, 1] }}
          whileInView="visible"
          viewport={{ once: true }}
          className="absolute w-[150vw] h-full bg-gradient-to-r from-offwhite from-75% to-transparent left-0 z-[1] pointer-events-none select-none bottom-0"
        ></motion.div>

        <svg className="absolute w-[30.5rem] h-auto scale-[.82] lg:scale-100 top-[-3.5rem] lg:top-[-.125rem] right-[-19.75rem] lg:right-[-0.1rem] z-[1]" viewBox="0 0 243 106" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M242.08 105.999V0H0C2.20914 0 4.00003 1.79086 4.00003 4V98C4.00003 100.209 5.79103 102 8.00006 102H238.001C240.209 102 242 103.79 242.001 105.999H242.08Z" fill="#F6F4E9"/>
        </svg>

        <motion.div
          className="absolute inset-0 h-full z-0 pointer-events-none select-none overflow-clip"
          initial={{ scale: 2.5 }}
          animate={{ scale: 1 }}
          transition={{
            duration: 1.25,
            ease: easeInOut,
          }}
          viewport={{ once: true, margin: '-100px' }}
        >
          <Image
            src={isMobile ? heroBackgroundMobile : heroBackground}
            alt="Hero Background"
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            quality={100}
            loading="lazy"
          />
        </motion.div>
        {/* Background End */}
      </div>
    </section>
  );
}
